// UserMenu — topbar trigger (name + role + avatar + chevron) + dropdown.
//
// Per shell-strings.md: opens below trigger, right-aligned, 248px wide,
// closes on outside-click / Esc / select. Trigger border turns brand when
// open. Contents: avatar + name + email · role swatch row · items (profile,
// preferences, shortcuts) · sign out (danger, separated).

import { useEffect, useRef, useState } from 'react';
import { Avatar, ChevronDownIcon, RoleBadge } from '@gc-coach/design-system';
import { isRoleKey, type RoleKey } from '@gc-coach/shared';
import { useAuth, useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { roleLabel } from './roleLabels.js';
import styles from './UserMenu.module.css';

export function UserMenu() {
  const { t } = useI18n();
  const { status, signOut } = useAuth();
  const { session, isLoading } = useSession();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside-click + Esc. Hook runs unconditionally so the rules of
  // hooks hold even on early returns below.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // UserMenu is only rendered inside an authenticated AdminShell. If session
  // isn't loaded yet, render a skeleton trigger so layout doesn't jump.
  if (status.kind !== 'authenticated') return null;
  if (!session) {
    return (
      <div className={styles.wrapper}>
        <button
          type="button"
          className={styles.trigger}
          aria-busy={isLoading}
          disabled
        >
          <span className={styles.triggerText}>
            <span className={styles.triggerName}>{t('shell.stateLoading')}</span>
          </span>
        </button>
      </div>
    );
  }

  const { displayName, email, roles } = session;
  // Primary role drives the badge — by Phase 0 convention, the first entry
  // in the user's role list is their primary. Backend orders deterministically.
  // session.roles is wire-typed as (RoleKey | ConsumerRoleKey)[] since the
  // same /auth/me response serves both portals; the admin SPA only ever sees
  // RoleKey entries so narrow defensively.
  const adminRoles = roles.filter(isRoleKey);
  const primaryRole: RoleKey = adminRoles[0] ?? 'read_only';

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={[styles.trigger, open ? styles.triggerOpen : null].filter(Boolean).join(' ')}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className={styles.triggerText}>
          <span className={styles.triggerName}>{displayName}</span>
          <span className={styles.triggerRole}>{roleLabel(primaryRole, t)}</span>
        </span>
        <Avatar name={displayName} email={email} size="sm" />
        <ChevronDownIcon
          width={10}
          height={10}
          className={[styles.chevron, open ? styles.chevronOpen : null].filter(Boolean).join(' ')}
        />
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <header className={styles.dropdownHeader}>
            <Avatar name={displayName} email={email} size="lg" />
            <div className={styles.dropdownHeaderText}>
              <span className={styles.dropdownName}>{displayName}</span>
              <span className={styles.dropdownEmail}>{email}</span>
            </div>
          </header>

          <div className={styles.dropdownRoleRow}>
            <RoleBadge role={primaryRole} label={roleLabel(primaryRole, t)} />
          </div>

          <ul className={styles.dropdownList} role="none">
            <li role="none">
              <button type="button" role="menuitem" className={styles.dropdownItem} disabled>
                {t('shell.userMenuMyProfile')}
              </button>
            </li>
            <li role="none">
              <button type="button" role="menuitem" className={styles.dropdownItem} disabled>
                {t('shell.userMenuPreferences')}
              </button>
            </li>
            <li role="none">
              <button type="button" role="menuitem" className={styles.dropdownItem} disabled>
                {t('shell.userMenuShortcuts')}
              </button>
            </li>
          </ul>

          <div className={styles.dropdownDanger}>
            <button
              type="button"
              role="menuitem"
              className={styles.dropdownSignOut}
              onClick={() => {
                setOpen(false);
                void signOut();
              }}
            >
              {t('shell.userMenuSignOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
